/////////////////////////////////////////////////////////////////////
// Copyright (c) Autodesk, Inc. All rights reserved
// Written by Autodesk Inventor Automation team
//
// Permission to use, copy, modify, and distribute this software in
// object code form for any purpose and without fee is hereby granted,
// provided that the above copyright notice appears in all copies and
// that both that copyright notice and the limited warranty and
// restricted rights notice below appear in all supporting
// documentation.
//
// AUTODESK PROVIDES THIS PROGRAM "AS IS" AND WITH ALL FAULTS.
// AUTODESK SPECIFICALLY DISCLAIMS ANY IMPLIED WARRANTY OF
// MERCHANTABILITY OR FITNESS FOR A PARTICULAR USE.  AUTODESK, INC.
// DOES NOT WARRANT THAT THE OPERATION OF THE PROGRAM WILL BE
// UNINTERRUPTED OR ERROR FREE.
/////////////////////////////////////////////////////////////////////
import 'bootstrap/dist/css/bootstrap.css';
import './theme.css';
import React from 'react';
import ReactDOM from 'react-dom';
import App from './App';
import {Provider} from 'react-redux';
import {createStore, applyMiddleware} from 'redux';
import { thunk } from 'redux-thunk';
import {mainReducer} from './reducers/mainReducer';
import "@hig/fonts/build/ArtifaktElement.css";

// Suppress expected/transient errors in development
const isSuppressibleError = (error, message) => {
    if (!error && !message) return false;
    const errorMsg = (error?.message || error?.toString() || message || '').toLowerCase();

    // SignalR connection closed - expected when jobs complete
    if (errorMsg.includes('connection being closed') ||
        errorMsg.includes('invocation canceled')) {
        return 'SignalR connection closed (expected)';
    }

    // Autodesk Viewer errors - intermittent library issues
    if (errorMsg.includes('pivotdisplaycallback') ||
        errorMsg.includes('autocam')) {
        return 'Viewer callback (transient)';
    }

    // Generic Script error from cross-origin or unmounted components
    if (message === 'Script error.' || errorMsg === 'script error.') {
        return 'Script error (cross-origin or async cleanup)';
    }

    return false;
};

window.addEventListener('unhandledrejection', function(event) {
    const suppressReason = isSuppressibleError(event.reason);
    if (suppressReason) {
        console.log('Suppressed:', suppressReason);
        event.preventDefault();
        event.stopImmediatePropagation();
        return false;
    }
});

window.addEventListener('error', function(event) {
    const suppressReason = isSuppressibleError(event.error, event.message);
    if (suppressReason) {
        console.log('Suppressed:', suppressReason);
        event.preventDefault();
        event.stopImmediatePropagation();
        return false;
    }
});

/* eslint-disable no-undef */
const createStoreWithMiddleware = applyMiddleware(thunk)(createStore);
const store = createStoreWithMiddleware(mainReducer);

// based on https://github.com/reduxjs/redux/issues/303#issuecomment-125184409
function observeStore(store, select, onChange) {
    let currentState;

    function handleChange() {
        const nextState = select(store.getState());
        if (nextState !== currentState) {
            onChange(currentState, nextState);
            currentState = nextState;
        }
    }

    const unsubscribe = store.subscribe(handleChange);
    handleChange();
    return unsubscribe;
}

const logSelector = (state) => state.notifications;

/** Dump log messages into browser console */
function logToConsole(oldMessages, newMessages) {
    const from = oldMessages ? oldMessages.length : 0;
    for (let i = from; i < newMessages.length; i++) {
        // eslint-disable-next-line no-console
        console.log(newMessages[i]);
    }
}

// listen for notifications changes and stream them into console
observeStore(store, logSelector, logToConsole);

const rootElement = document.getElementById('root');
ReactDOM.render(
    <Provider store={store}>
        <App />
    </Provider>, rootElement
);